import { EventStarterKeysSchema } from '@racemap/sdk/schema/eventStarterKey';
import { type EventsQuery, RacemapAPIClient } from '@racemap/utilities/api-client';
import { EventTypes } from '@racemap/utilities/consts/events';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { produce } from 'immer';
import type { StoreApi } from 'zustand';
import { Tristate } from '../../../components/utils/tristateTask';
import type { DraftState, State } from '../../reducers';
import { prepareFrontendBillableItems } from '../events_reducer_helper';
import type { CurrentEvent, EventsActions } from '../events_reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const createLoadActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<
  EventsActions,
  | 'loadEvents'
  | 'loadEvent'
  | 'loadChildEvents'
  | 'loadCurrentEvent'
  | 'loadCurrentStarters'
  | 'clearCurrentEvent'
> => ({
  loadEvents: async ({ filter = 'can-edit', show = ['hidden'], ...options }) => {
    set(
      produce((s) => {
        s.events.isLoading = true;
      }),
    );
    const events = await apiClient.getEvents({
      filter,
      show,
      ...options,
    });

    set(
      produce((s) => {
        for (const event of events) {
          s.events.items.set(event.id, event);
        }

        s.events.isLoading = false;
        s.events.isReady = true;
      }),
    );
  },
  loadEvent: async (eventId) => {
    const event = await apiClient.getEvent(eventId);

    set(
      produce((s) => {
        s.events.items.set(eventId, event);
      }),
    );
    return event;
  },
  loadChildEvents: async (
    parentId,
    { filter = 'can-edit', show = ['hidden'] }: EventsQuery = {},
  ) => {
    const childEvents = await apiClient.getEvents({
      filter,
      show,
      findByParentId: parentId,
    });

    set(
      produce((s) => {
        for (const event of childEvents) {
          s.events.items.set(event.id, event);
        }
      }),
    );
  },
  loadCurrentEvent: async (eventId) => {
    const [event, starters, geos, keys, billableItems, userCredits] = await Promise.all([
      apiClient.getEvent(eventId),
      apiClient.getEventStarters(eventId, { noFilter: true }),
      apiClient.getEventGeo(eventId),
      apiClient.getEventStarterKeys(eventId),
      apiClient.getEventBillableItems(eventId),
      apiClient.getEventUserCredits(eventId),
    ]);

    let childEvents: Array<RacemapEvent> = [];
    if (event.type === EventTypes.STAGE_GROUP) {
      childEvents = await apiClient.getStageGroupStages(event.id);
    } else if (event.type === EventTypes.CONTEST_GROUP) {
      childEvents = await apiClient.getContestGroupContests(event.id);
    }

    if (
      (event.type === EventTypes.CONTEST || event.type === EventTypes.STAGE) &&
      event.parent != null
    ) {
      await get().events.actions.loadEvent(event.parent);
    }
    // append billing cycle infos to billable items
    const preparedBillableItems = prepareFrontendBillableItems(billableItems, event);
    const keysArray = EventStarterKeysSchema.parse(keys);

    const currentEvent: CurrentEvent = {
      ...event,
      keys: new Map(keysArray.map((key) => [key.key, key])),
      starters: new Map(starters.map((starter) => [starter.id, starter])),
      geo: {
        ...event.geo,
        features: geos.features,
        shadowtrackId: event.geo?.shadowtrackId || null,
      },
      billableItems: preparedBillableItems,
      childEvents,
      userCredits,
    };

    set(
      produce((s) => {
        s.events.currentEventId = event.id;
        s.events.items.set(event.id, currentEvent);
        for (const child of childEvents) {
          s.events.items.set(child.id, child);
        }
      }),
    );
    return event;
  },
  loadCurrentStarters: async (eventId) => {
    const [starters, keys] = await Promise.all([
      apiClient.getEventStarters(eventId, { noFilter: true }),
      apiClient.getEventStarterKeys(eventId),
    ]);

    let currentEventState = get().events.items.get(eventId);
    if (currentEventState == null) {
      currentEventState = await get().events.actions.loadEvent(eventId);
    }

    if (currentEventState == null) {
      throw new Error(`Fail Event Update. Event with eventId ${eventId} is unknown.`);
    }

    const keysMap = new Map(keys.map((key) => [key.key, key]));
    const startersMap = new Map(starters.map((starter) => [starter.id, starter]));

    set(
      produce((s) => {
        if (currentEventState == null) return;

        s.events.currentEventId = currentEventState.id;
        s.events.items.set(currentEventState.id, {
          ...currentEventState,
          starters: startersMap,
          keys: keysMap,
        });
      }),
    );
  },
  clearCurrentEvent: () => {
    set(
      produce((s: DraftState) => {
        s.events.currentEventId = null;
        s.events.savingState = Tristate.NORMAL;
        s.events.lastStarterSyncReport = null;
      }),
    );
  },
});
